import "@/App.css";
import "lenis/dist/lenis.css";
import { useEffect, useRef } from "react";
import Lenis from "lenis";
import HeroSection from "@/components/HeroSection";
import JourneySection from "@/components/JourneySection";
import SparklLiveSection from "@/components/SparklLiveSection";
import SparklAITutorSection from "@/components/SparklAITutorSection";
import ParentTestimonialsSection from "@/components/ParentTestimonialsSection";
import SparklCoursesSection from "@/components/SparklCoursesSection";
import FoundersSection from "@/components/FoundersSection";
import WebsiteFooter from "@/components/WebsiteFooter";
import { emitLenisScroll } from "@/lenisScrollBridge";
import { applyJourneyScrollGate } from "@/journeyScrollGate";
import {
  getActiveMobileScrollGate,
  softenMobileScrollDeltaAtBounds,
} from "@/mobileScrollGate";
import {
  beginMobileAnchorNavigation,
  MOBILE_ANCHOR_NAV_EVENT,
} from "@/mobileAnchorNav";
import { attachMobileTouchReleaseCoast } from "@/mobileTouchCoast";
import {
  attachMobileSectionSnap,
  sectionSnapScrollY,
} from "@/mobileSectionSnap";
import {
  MOBILE_MQ,
  anchorScrollDuration,
  anchorScrollEasing,
  buildDesktopLenisOptions,
  buildMobileLenisOptions,
  prefersReducedMotion,
} from "@/scrollPresets";

/**
 * Desktop: Lenis wheel only. Mobile: smooth touch + capped release coast — scrollPresets.js.
 */

function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    let rafId = 0;
    let onLenisScroll = null;
    let detachMobileCoast = () => {};
    let detachMobileSnap = () => {};

    const anchorBelowFixedNav = () =>
      Math.min(112, Math.max(82, window.innerHeight * 0.12));

    const anchorExtraVhFraction = () => window.innerHeight * 0.1;

    /** Desktop only — section offsets below fixed nav (unchanged web behavior). */
    const DESKTOP_SECTION_OFFSETS = {
      "#journey": () => anchorBelowFixedNav() + anchorExtraVhFraction(),
      "#programs": anchorBelowFixedNav,
      "#testimonials": anchorBelowFixedNav,
      "#founders": anchorBelowFixedNav,
      "#live": () => window.innerHeight * 0.18 + anchorExtraVhFraction(),
      "#ai-tutor": () => window.innerHeight * 0.45 + anchorExtraVhFraction(),
    };

    const MOBILE_ANCHOR_TOP_TOLERANCE_PX = 4;

    const alignMobileSectionTop = (target, lenis) => {
      const drift = Math.abs(target.getBoundingClientRect().top);
      if (drift <= MOBILE_ANCHOR_TOP_TOLERANCE_PX) return;
      lenis.scrollTo(sectionSnapScrollY(target, lenis), {
        immediate: true,
        force: true,
        programmatic: true,
      });
    };

    const finishMobileAnchor = (target, lenis, hash) => {
      alignMobileSectionTop(target, lenis);
      window.dispatchEvent(
        new CustomEvent(MOBILE_ANCHOR_NAV_EVENT, { detail: { hash } }),
      );
    };

    const handleAnchorClick = (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const hash = link.getAttribute("href");
      if (!hash || hash === "#") return;
      const target = document.querySelector(hash);
      if (!target) return;
      e.preventDefault();

      const lenis = lenisRef.current;
      if (!lenis) return;
      const isMobile = mq.matches;
      const reduced = prefersReducedMotion();
      const duration = anchorScrollDuration(isMobile);

      // Mobile only — viewport-top alignment + section starting-state reset.
      // Desktop keeps DESKTOP_SECTION_OFFSETS below (unchanged web behavior).
      if (isMobile) {
        if (!reduced) {
          beginMobileAnchorNavigation(lenis, duration, hash);
        } else {
          beginMobileAnchorNavigation(lenis, 0, hash);
        }

        const y = sectionSnapScrollY(target, lenis);
        lenis.scrollTo(y, {
          immediate: reduced,
          duration,
          easing: anchorScrollEasing(true),
          force: true,
          programmatic: true,
          onComplete: () => finishMobileAnchor(target, lenis, hash),
        });
        if (reduced) {
          requestAnimationFrame(() => finishMobileAnchor(target, lenis, hash));
        }
        return;
      }

      const offsetFn = DESKTOP_SECTION_OFFSETS[hash];
      const offset = offsetFn ? offsetFn() : 0;
      lenis.scrollTo(target, {
        immediate: reduced,
        duration,
        easing: anchorScrollEasing(false),
        offset,
        force: true,
        programmatic: true,
      });
    };

    const startLenisRaf = (lenis, mobile) => {
      const step = (time) => {
        lenis.raf(time);
        if (mobile) applyJourneyScrollGate(lenis);
        rafId = requestAnimationFrame(step);
      };
      rafId = requestAnimationFrame(step);
    };

    const destroyLenis = () => {
      detachMobileCoast();
      detachMobileSnap();
      detachMobileCoast = () => {};
      detachMobileSnap = () => {};
      if (lenisRef.current && onLenisScroll) {
        lenisRef.current.off("scroll", onLenisScroll);
        onLenisScroll = null;
      }
      cancelAnimationFrame(rafId);
      rafId = 0;
      lenisRef.current?.destroy();
      lenisRef.current = null;
      document.documentElement.classList.remove("lenis", "lenis-smooth");
    };

    const attachScroll = () => {
      destroyLenis();
      const isMobile = mq.matches;
      const lenis = new Lenis(
        isMobile ? buildMobileLenisOptions() : buildDesktopLenisOptions(),
      );
      if (isMobile) {
        const baseVirtual = lenis.options.virtualScroll;
        lenis.options.virtualScroll = (data) => {
          if (baseVirtual) baseVirtual(data);
          const bounds = getActiveMobileScrollGate();
          if (bounds) {
            const maxY = Math.min(
              bounds.maxScrollY,
              bounds.sectionEnd ?? bounds.maxScrollY,
            );
            data.deltaY = softenMobileScrollDeltaAtBounds(
              data.deltaY,
              lenis.scroll,
              { minScrollY: bounds.minScrollY, maxScrollY: maxY },
            );
          }
          return true;
        };
      }
      lenisRef.current = lenis;
      onLenisScroll = emitLenisScroll;
      lenis.on("scroll", onLenisScroll);
      startLenisRaf(lenis, isMobile);
      if (isMobile && !prefersReducedMotion()) {
        detachMobileCoast = attachMobileTouchReleaseCoast(lenis);
        detachMobileSnap = attachMobileSectionSnap(lenis);
      }
    };

    document.addEventListener("click", handleAnchorClick);
    attachScroll();
    mq.addEventListener("change", attachScroll);

    let resizeScheduled = false;
    const scheduleResizeForLenis = () => {
      if (!lenisRef.current) return;
      if (resizeScheduled) return;
      resizeScheduled = true;
      requestAnimationFrame(() => {
        resizeScheduled = false;
        lenisRef.current?.resize();
      });
    };

    window.addEventListener("resize", scheduleResizeForLenis, { passive: true });

    const reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onReduceMotion = () => attachScroll();
    reduceMq.addEventListener("change", onReduceMotion);

    return () => {
      reduceMq.removeEventListener("change", onReduceMotion);
      mq.removeEventListener("change", attachScroll);
      window.removeEventListener("resize", scheduleResizeForLenis);
      document.removeEventListener("click", handleAnchorClick);
      destroyLenis();
    };
  }, []);

  return (
    <div className="App">
      <HeroSection />
      <SparklLiveSection />
      <SparklAITutorSection />
      <JourneySection />
      <ParentTestimonialsSection />
      <SparklCoursesSection />
      <FoundersSection />
      <WebsiteFooter />
    </div>
  );
}

export default App;
